/* eslint-disable react/prop-types */
import { useCallback, useMemo, useState } from 'react';
import { Loadable } from 'foundations-components/transitional/utils/Loadable';
import ListingLibLayout from 'framework-listing-lib/layout/ListingLibLayout';
import useCrmObjectTypeListingFilters from 'framework-listing-lib/hooks/useCrmObjectTypeListingFilters';
import { useFilterQuery } from 'framework-listing-lib/hooks/useListingLibFilters';
import useExpandCollapseRow from 'framework-listing-lib/hooks/useExpandCollapseRow';
import useFetchCrmObjectType from 'framework-listing-lib/hooks/useFetchCrmObjectType';
// @ts-expect-error Untyped module
import { useReviewData } from 'content-label-lib/hooks/useReviewData';
import { CONTENT_PLATFORM_PROPERTY_NAME, contentHasTranslations, getAllUniqueContentIdsFromCrmObjects, getContentPropertyValue } from 'content-management-data-lib';
import ContentBulkActions from 'content-management-actions-lib/components/ContentBulkActions';
import NestedContentRow from './NestedContent';
import useRenderCustomColumn from '../hooks/useRenderCustomColumn';
import TableActions from './TableActions';
import ContentEmptyState from './ContentEmptyState';
import useTableColumns from '../hooks/useTableColumns';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const PreviewPanel = Loadable({
  loader: () => import( /* webpackChunkName: "preview-panel" */'./PreviewPanel').then(mod => mod.default),
  LoadingComponent: () => null
});
const ContentListingTable = ({
  additionalTableActions,
  renderSuccessMarker,
  renderErrorMarker,
  pqlRowProps,
  hasContentPreviewPanel,
  hasBulkActions,
  CustomEmptyState,
  propertyTableColumnConfigs
}) => {
  const {
    data
  } = useFetchCrmObjectType();
  const filterQuery = useFilterQuery();
  const {
    hasAppliedFilters
  } = useCrmObjectTypeListingFilters();
  const {
    expandableObjectIds
  } = useExpandCollapseRow();
  const reviewData = useReviewData(data && data.results ? getAllUniqueContentIdsFromCrmObjects(data.results) : [], 'webpage');
  const isFilteringOrSearching = useMemo(() => !!filterQuery || hasAppliedFilters, [filterQuery, hasAppliedFilters]);
  const renderCustomColumn = useRenderCustomColumn({
    reviewData,
    propertyTableColumnConfigs
  });
  const columns = useTableColumns();
  const [isPreviewPanelOpen, setIsPreviewPanelOpen] = useState(false);
  const [{
    previewPanelCrmObject,
    previewPanelParentCrmObject
  }, setPreviewPanelCrmObjects] = useState({});
  const toggleChildRowsSelection = useMemo(() => ({
    crmObject
  }) => {
    const translatedFromId = getContentPropertyValue(crmObject, CONTENT_PLATFORM_PROPERTY_NAME.TRANSLATED_FROM_ID);
    return (
      /**
       * First condition is to select all translations
       * when primary is selected and
       * user `is not` filtering or searching
       */
      !isFilteringOrSearching && contentHasTranslations(crmObject) || (
      /**
       * Second condition is to select all translations
       * when primary is selected and
       * user `is` filtering or searching
       */
      isFilteringOrSearching && expandableObjectIds.includes(crmObject.objectId)) || (
      /**
       * Last condition is to select
       * all translations and primary
       * when a translation is selected
       * regardless of search and filter
       */
      !!translatedFromId && expandableObjectIds.includes(translatedFromId))
    );
  }, [expandableObjectIds, isFilteringOrSearching]);
  const rowProps = useMemo(() => ({
    onClick: ({
      crmObject,
      parentCrmObject
    }) => {
      if (hasContentPreviewPanel) {
        // open preview panel
        setIsPreviewPanelOpen(true);
        setPreviewPanelCrmObjects({
          previewPanelCrmObject: crmObject,
          previewPanelParentCrmObject: parentCrmObject
        });
      }
    },
    rowSize: 'medium',
    expandOnSelect: ({
      crmObject
    }) => !isFilteringOrSearching && contentHasTranslations(crmObject),
    selectChildRowsOnParentSelect: toggleChildRowsSelection,
    unselectChildRowsOnParentUnselect: toggleChildRowsSelection,
    selectTopLevelRowOnNestedRowSelect: true,
    unselectTopLevelRowOnNestedRowUnselect: true
  }), [hasContentPreviewPanel, isFilteringOrSearching, toggleChildRowsSelection]);
  const bulkActionProps = useMemo(() => ({
    canSelectAllAssets: false,
    hasBulkActions,
    renderBulkActions: ({
      selectedObjects
    }) => /*#__PURE__*/_jsx(ContentBulkActions, {
      crmObjects: selectedObjects
    })
  }), [hasBulkActions]);
  const renderEmptyState = props => {
    return /*#__PURE__*/_jsx(ContentEmptyState, {
      emptyStateProps: props,
      CustomEmptyState: CustomEmptyState
    });
  };
  const renderPqlRow = useCallback(({
    page,
    rowIndex
  }) => {
    if (page !== 0 || rowIndex !== 0 || !pqlRowProps || pqlRowProps && !pqlRowProps.shouldRenderPql) {
      return null;
    }
    return /*#__PURE__*/_jsx(pqlRowProps.UIUpgradeBanner, {
      upgradeKey: pqlRowProps.pqlUpgradeKey
    });
  }, [pqlRowProps]);
  return /*#__PURE__*/_jsxs("div", {
    "data-onboarding": "content-table-wrapper",
    children: [/*#__PURE__*/_jsx(ListingLibLayout, {
      maxHeight: "auto",
      actionsComponent: /*#__PURE__*/_jsx(TableActions, {
        additionalTableActions: additionalTableActions
      }),
      rowProps: rowProps,
      bulkActionProps: bulkActionProps,
      columnKeys: columns,
      renderCustomColumn: renderCustomColumn,
      renderEmptyState: renderEmptyState,
      renderPqlRow: renderPqlRow,
      renderNestedContent: props => /*#__PURE__*/_jsx(NestedContentRow, Object.assign({}, props)),
      renderSuccessMarker: renderSuccessMarker,
      renderErrorMarker: renderErrorMarker
    }), isPreviewPanelOpen ? /*#__PURE__*/_jsx(PreviewPanel, {
      crmObject: previewPanelCrmObject,
      parentCrmObject: previewPanelParentCrmObject,
      closePreviewPanel: () => setIsPreviewPanelOpen(false)
    }) : null]
  });
};
export default ContentListingTable;